import { Center, Column, Spinner, Typography } from '@tempmee/react';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_JOB_ACTION, fetchJob } from '~/actions';
import { colors } from '@tempmee/tailwind';
import { ShiftDetails } from '~/components/ConfirmCandidate/components/ShiftDetails';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ICounterOfferEntity } from '~/domain/Jobs';
import { useLoading } from '~/hooks';
import { IMultipleCounters } from '~/models/Job';

interface IInterviewDetailsModalProps {
  counterOffer: ICounterOfferEntity;
  interviewId: string;
  professionalId: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
}

export const InterviewDetailsModal = ({
  counterOffer,
  interviewId,
  isLoading = false,
  professionalId,
  onClose,
  onConfirm,
}: IInterviewDetailsModalProps) => {
  const dispatch = useDispatch();
  const [notes, setNotes] = useState('');

  const { isLoading: isJobLoading } = useLoading([FETCH_JOB_ACTION]);

  useEffect(() => {
    dispatch(
      fetchJob({
        jobId: interviewId,
        candidateId: professionalId,
        requestCandidateOvertime: false,
      }),
    );
  }, [dispatch, interviewId, professionalId]);

  const currentCounter = useMemo(
    () =>
      ({
        id: counterOffer.id,
        candidateRate: counterOffer.rate,
        expiration: counterOffer.expiration as unknown as Date,
        breakTime: counterOffer.breakTime,
        originalBreakTime: counterOffer.originalBreakTime,
        local_begin: format(new Date(counterOffer.begin), 'hh:mm aa'),
        local_end: format(new Date(counterOffer.end), 'hh:mm aa'),
        payment: counterOffer.payment,
      }) as IMultipleCounters,
    [counterOffer],
  );

  return (
    <ConfirmPopup
      closePopup={onClose}
      leftButtonAction={onClose}
      leftButtonDisabled={isLoading || isJobLoading}
      leftButtonText="Go Back"
      rightButtonAction={() => onConfirm(notes)}
      rightButtonDisabled={isLoading || isJobLoading}
      rightButtonLoading={isLoading}
      rightButtonText="Confirm"
      title="Interview Details"
      modalStyle={{
        paddingInline: 54,
        width: 500,
      }}
    >
      {isJobLoading ? (
        <Center direction="column" pt={6} pb={12}>
          <Spinner centered variant="default" size="md" />
        </Center>
      ) : (
        <Column width="100%" style={{ marginBottom: 26 }}>
          <ShiftDetails currentCounter={currentCounter} hasBorderStyle={false} isLiteVersion />

          <Column gap={2} style={{ marginTop: 24 }}>
            <Typography as="span" bold>Include Optional Notes:</Typography>
            <textarea
              className="text_notes"
              style={{
                borderWidth: 0,
                borderRadius: 10,
                backgroundColor: colors.neutral[50],
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.25) inset',
                padding: '12px 20px',
                width: 'auto',
                height: 80,
              }}
              placeholder="Notes for Dental Professional"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Column>
        </Column>
      )}
    </ConfirmPopup>
  );
};
